import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import {
  transformConfigurationResponseHandler,
  transformResponseHandler,
} from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const userApi = createApi({
  reducerPath: REDUCER_PATHS.USERS,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    adminManagementListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.LISTING_ADMIN_MANAGEMENT}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
    }),
    archivedUsersListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.ARCHIVED_USER}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&accountType=${data?.accountType}`,
        method: "GET",
      }),
    }),
    addAdminUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADMIN_MANAGEMENT,
        body: data,
        method: "POST",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editAdminUser: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.ADMIN_MANAGEMENT}/${data?.id}`,
        body: data?.postData,
        method: "PUT",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteAdminUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADMIN_MANAGEMENT,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    statusChangeAdminUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.ADMIN_MANAGEMENT,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    detailsAdminUser: builder.query({
      query: (data) => ({
        url: `${apiConfig.ADMIN_MANAGEMENT}/${data.adminId}`,
        method: "GET",
      }),
    }),
    userManagementListing: builder.query({
      query: (data) => {
        const {
          page,
          pageSize,
          searchText,
          sortColumn,
          sortDirection,
          languageId,
          filter,
          isDeleted,
        } = data || {};
        let url = `${apiConfig.LISTING_USER_MANAGEMENT}?page=${page}&pageSize=${pageSize}&searchText=${searchText}&sortColumn=${sortColumn}&sortDirection=${sortDirection}&languageId=${languageId}&isDeleted=${isDeleted}&filter=${filter}`;
        return {
          url,
          method: "GET",
        };
      },
    }),
    individualUserDetails: builder.query({
      query: (data) => {
        return {
          url: `${apiConfig.INDIVIDUAL_USER_DETAILS}/${data}`,
          method: "GET",
        };
      },
      providesTags: ["userDetails"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserDocuments: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_DOCUMENTS}?page=${data?.page}&pageSize=${data?.pageSize}&categoryName=${data.categoryName}&userId=${data.userId}`,
        method: "GET",
      }),
      providesTags: ["userDocuments"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserBankAccounts: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_BANK_ACCOUNTS}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["userBankAccounts"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserInvestments: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_INVESTMENTS}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&requestStatus=${data?.requestStatus}`,
        method: "GET",
      }),
      providesTags: ["userInvestments"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserPersonalDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_PERSONAL_DETAILS}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["userPersonalDetails"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserPersonalSingleDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_PERSONAL_SINGLE_DETAILS}/${data.personalsId}`,
        method: "GET",
      }),
      providesTags: ["userPersonalSingleDetails"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserDeposits: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_DEPOSITS}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["userDeposits"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserWithdrawals: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_WITHDRAWALS}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["userWithdrawals"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserTimeLine: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_TIME_LINE}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["userTimeLine"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserReturns: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_RETURNS}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&returnType=${data?.returnType}`,
        method: "GET",
      }),
      providesTags: ["userReturns"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserReturnStatistics: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_RETURNS_STATISTICS}?userId=${data.userId}`,
        method: "GET",
      }),
      providesTags: ["userReturns"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserRiskMeters: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_RISKMETER}/${data.userId}`,
        method: "GET",
      }),
      providesTags: ["userRiskMeters"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserIncomeInformation: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_INCOME_INFORMATION}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&sortColumn=createdAt&sortDirection=-1`,
        method: "GET",
      }),
      providesTags: ["userIncomeInformation"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    addIndividualUserDocument: builder.mutation({
      query: (data) => {
        const { userId, ...rest } = data;
        return {
          url: `${apiConfig.INDIVIDUAL_USER_DETAILS_DOCUMENTS_ADD}/${userId}`,
          body: rest,
          method: "POST",
        };
      },
      invalidatesTags: ["userDocuments"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    editIndividualUserDocument: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_DOCUMENTS_EDIT}?userId=${data.userId}`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: ["userDocuments"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteIndividualUserDocument: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.INDIVIDUAL_USER_DETAILS_DOCUMENTS_DELETE}/${data.documentId}`,
        body: data,
        method: "DELETE",
      }),
      invalidatesTags: ["userDocuments"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT,
        body: data?.postData || data,
        method: "POST",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT + "/" + data?.userId,
        body: data?.postData,
        method: "PUT",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    deleteUser: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT_DELETE,
        body: data,
        method: "DELETE",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    changeUserStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_MANAGEMENT_STATUS_UPDATE,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    userDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.USER_MANAGEMENT}?userCode=${data?.userCode}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["userDetails"],
    }),
    userDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.USER_MANAGEMENT_DOWNLOAD}/${data.type}?languageId=${data?.languageId}&isDeleted=true&filter=[{"key":"accountType","type":"contains","value":"individual","condition":"$and"}]&column=${data.column}`,
        method: "GET",
      }),
    }),
    userBusinessDetailsDownload: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.USER_MANAGEMENT_DOWNLOAD}/${data.type}?languageId=${data?.languageId}&isDeleted=true&filter=[{"key":"accountType","type":"contains","value":"business","condition":"$and"}]&column=${data.column}`,
        method: "GET",
      }),
    }),
    adminUserDetailsDownload: builder.mutation({
      query: (data) => {
        const params = new URLSearchParams();
        if (data.filter) params.append("filter", data.filter);
        if (data.column) params.append("column", data.column);
        return {
          url: `${apiConfig.ADMIN_USERS_DOWNLOAD}/${
            data.type
          }?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    changeBusinessUserStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_APPROVAL_STATUS_UPDATE,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    dailyUserReport: builder.query({
      query: (data) => ({
        url: `${apiConfig.USER_DAILY_REPORT}?filter=${data?.filter}`,
        method: "GET",
      }),
      providesTags: ["dailyUserReport"],
    }),
    changeIndividualKycStatusUpdate: builder.mutation({
      query: (data) => ({
        url: apiConfig.USER_KYC_STATUS_UPDATE,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: ["userDetails"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    individualUserKYCHistoryListing: builder.query({
      query: (data) => ({
        url: `${apiConfig.USER_KYC_HISTORY_LISTING}?userId=${data.userId}&page=${data?.page}&pageSize=${data?.pageSize}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["userDetails"],
      transformResponse: (resp) => transformConfigurationResponseHandler(resp),
    }),
    updateBusinessCustomUserDocument: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.UPDATE_BUSINESS_CUSTOM_USER_DOCUMENT}/${data.userId}`,
        body: data,
        method: "PUT",
      }),
    }),
  }),
});

export const {
  useLazyAdminManagementListingQuery,
  useAddAdminUserMutation,
  useDeleteAdminUserMutation,
  useStatusChangeAdminUserMutation,
  useDetailsAdminUserQuery,
  useLazyUserManagementListingQuery,
  useLazyIndividualUserDetailsQuery,
  useLazyIndividualUserDocumentsQuery,
  useLazyIndividualUserIncomeInformationQuery,
  useAddUserMutation,
  useDeleteUserMutation,
  useChangeUserStatusMutation,
  useLazyUserDetailsQuery,
  useUserDetailsDownloadMutation,
  useUserBusinessDetailsDownloadMutation,
  useAdminUserDetailsDownloadMutation,
  useEditUserMutation,
  useEditAdminUserMutation,
  useAddIndividualUserDocumentMutation,
  useEditIndividualUserDocumentMutation,
  useDeleteIndividualUserDocumentMutation,
  useLazyArchivedUsersListingQuery,
  useLazyIndividualUserBankAccountsQuery,
  useLazyIndividualUserInvestmentsQuery,
  useLazyIndividualUserPersonalDetailsQuery,
  useLazyIndividualUserPersonalSingleDetailsQuery,
  useLazyIndividualUserWithdrawalsQuery,
  useLazyIndividualUserDepositsQuery,
  useLazyIndividualUserRiskMetersQuery,
  useLazyIndividualUserReturnsQuery,
  useLazyIndividualUserReturnStatisticsQuery,
  useLazyIndividualUserTimeLineQuery,
  useLazyDailyUserReportQuery,
  useChangeBusinessUserStatusMutation,
  useLazyIndividualUserKYCHistoryListingQuery,
  useChangeIndividualKycStatusUpdateMutation,
  useUpdateBusinessCustomUserDocumentMutation,
} = userApi;

export default userApi;
